/**
 * Shared presentation for WhatsApp / Meta delivery failure codes.
 * Keeps campaign results, reports, and message detail wording aligned.
 */

export type WhatsAppDeliveryFailureCopy = {
  primary: string;
  secondary: string | null;
  technicalCode: string;
};

type Translate = (key: string, options?: Record<string, unknown>) => string;

const FAILURE_CODE_KEY_PREFIX = "campaigns.results.failureCodes.";
const FAILURE_SUPPORT_KEY_PREFIX = "campaigns.results.failureCodeSupport.";

/** Application failure codes are SCREAMING_SNAKE; Meta prose titles are not. */
export function looksLikeDeliveryFailureCode(value: string): boolean {
  return /^[A-Z][A-Z0-9_]*$/.test(value);
}

export function resolveWhatsAppDeliveryFailureCopy(
  t: Translate,
  failureCode: string | null | undefined,
): WhatsAppDeliveryFailureCopy | null {
  if (failureCode == null) return null;
  const trimmed = failureCode.trim();
  if (trimmed.length === 0) return null;

  const primaryKey = `${FAILURE_CODE_KEY_PREFIX}${trimmed}`;
  const translated = t(primaryKey);
  const primary =
    translated !== primaryKey ? translated : t(`${FAILURE_CODE_KEY_PREFIX}generic`);

  const supportKey = `${FAILURE_SUPPORT_KEY_PREFIX}${trimmed}`;
  const support = t(supportKey);
  const secondary = support !== supportKey ? support : null;

  return {
    primary,
    secondary,
    technicalCode: trimmed,
  };
}

/**
 * Localize a report `failureReason` when it is an application error code.
 * Leaves free-text provider/job errors unchanged.
 */
export function localizeDeliveryFailureReason(
  t: Translate,
  failureReason: string | null | undefined,
): string | null {
  if (failureReason == null) return null;
  const trimmed = failureReason.trim();
  if (trimmed.length === 0) return null;
  if (!looksLikeDeliveryFailureCode(trimmed)) return trimmed;
  return resolveWhatsAppDeliveryFailureCopy(t, trimmed)?.primary ?? trimmed;
}
